import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/collections", label: "Collections" },
  { to: "/custom-orders", label: "Custom Orders" },
  { to: "/queries", label: "Queries" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
          scrolled
            ? "bg-[#F5F2EA]/85 backdrop-blur-xl border-b border-[#D5D0C5] py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="Arkaari home">
            <Logo size="text-xl sm:text-2xl" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`overline link-underline transition-opacity ${
                  location.pathname === l.to ? "text-[#C6A15B]" : "text-[#121212] hover:opacity-70"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/custom-orders"
              data-testid="nav-custom-cta"
              className="hidden sm:inline-flex btn-gold px-5 py-2.5 overline"
            >
              <span>Custom Order</span>
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-[#121212]"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[#121212] text-[#F5F2EA] lg:hidden flex flex-col justify-center px-8 grain"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-serif text-4xl font-light py-1 block"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <Link to="/custom-orders" className="btn-gold mt-8 px-6 py-4 overline text-center">
                <span>Request a Custom Order</span>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
